import { styled } from '@linaria/react';
import { Button, Modal, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { useCreateGoalPresentation } from './presentation-modal.hooks';
import type { GoalPresentationModalProps } from './presentation-modal.type';

export const GoalPresentationModal = ({ opened, onClose, goalIds, goalsCount }: GoalPresentationModalProps) => {
  const i18n = useTranslation();
  const {
    presentationName,
    setPresentationName,
    isCreatingPresentation,
    hasGoogleCreds,
    createPresentation,
    getGoogleAuthUrl,
  } = useCreateGoalPresentation(goalIds);

  const handleClose = () => {
    setPresentationName('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!presentationName) return;

    try {
      const presentationUrl = await createPresentation();

      window.alert(i18n.t('strategy.goals.presentation.created_success', { name: presentationName }));
      window.open(presentationUrl, '_blank', 'noopener, noreferrer');

      handleClose();
    } catch (error) {
      window.alert(
        i18n.t('strategy.goals.presentation.creation_error', {
          error: error instanceof Error ? error.message : 'Failed to create presentation',
        }),
      );
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={i18n.t('strategy.goals.presentation.title')} size="md">
      <ModalContent>
        {!hasGoogleCreds && (
          <Text color="red" size="small">
            {i18n.t('strategy.goals.presentation.auth_required')}
          </Text>
        )}

        <TextInput
          label={i18n.t('strategy.goals.presentation.name_label')}
          placeholder={i18n.t('strategy.goals.presentation.name_placeholder')}
          value={presentationName}
          onChange={(e) => setPresentationName(e.currentTarget.value)}
          required
        />

        <Text size="small">
          {i18n.t('strategy.goals.presentation.will_create_prefix')}{' '}
          {goalsCount > 5 ? (
            <Tooltip label={i18n.t('strategy.goals.presentation.count_warning')} variant="white" zIndex={1000}>
              <span style={{ color: 'var(--red-base)', fontWeight: 'bold', cursor: 'pointer' }}>{goalsCount}*</span>
            </Tooltip>
          ) : (
            goalsCount
          )}{' '}
          {i18n.t(`common.${goalsCount === 1 ? 'strategic_goal' : 'strategic_goals'}`).toLowerCase()}
        </Text>

        <ButtonContainer>
          {!hasGoogleCreds && (
            <Button
              variant="outline"
              onClick={() => {
                window.location.href = getGoogleAuthUrl();
              }}
            >
              {i18n.t('strategy.goals.presentation.connect_google')}
            </Button>
          )}
          <Button variant="outline" onClick={handleClose}>
            {i18n.t('common.actions.cancel')}
          </Button>
          <Button
            loading={isCreatingPresentation}
            disabled={!presentationName || !hasGoogleCreds}
            onClick={handleSubmit}
          >
            {i18n.t('common.actions.create')}
          </Button>
        </ButtonContainer>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;
